import {
  Mic,
  MicOff,
  Video,
  VideoOff,
  MessageSquare,
  Camera,
  FileText,
  Edit3,
  Square,
  Circle,
  Phone,
  RefreshCcw,
  X,
  MoreVertical,
  Loader2,
  Languages,
} from 'lucide-react';
import { useState, useRef, useEffect } from 'react';

const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Spanish' },
  { code: 'hi', label: 'Hindi' },
];

interface VideoCallToolbarProps {
  isMobileView: boolean;
  isHost: boolean;
  isMuted: boolean;
  isVideoOff: boolean;
  isRecording: boolean;
  activePanel: 'chat' | 'notes' | 'annotate' | 'measure' | null;
  hasUnreadMessages: boolean;
  savingSnapshot: boolean;
  showMobileMenu: boolean;
  isGuestMeasuring?: boolean; // For blurring toolbar when guest is measuring
  isTranslationEnabled?: boolean; // Host only - live translation toggle state
  isTranslating?: boolean; // true when actively streaming audio to OpenAI
  hostLanguage?: string; // Host's spoken language ('en' or 'es')
  guestLanguage?: string; // Guest's spoken language ('en' or 'es')
  toggleTranslation?: () => void; // Host only - stop translation (when already active)
  onStartTranslation?: (hostLang: string, guestLang: string) => void; // Host only - start with selected languages
  setShowMobileMenu: (show: boolean) => void;
  setActivePanel: (panel: 'chat' | 'notes' | 'annotate' | 'measure' | null) => void;
  setHasUnreadMessages: (has: boolean) => void;
  toggleMute: () => void;
  toggleVideo: () => void;
  toggleRecording: () => void;
  takeSnapshot: () => void;
  switchCamera: () => void;
  handleEndCall: () => void;
  setIsAnnotating: (val: boolean) => void;
  setSelectedAnnotationTool: (tool: 'pencil' | 'square' | 'circle' | 'arrow' | null) => void;
}

export function VideoCallToolbar({
  isMobileView,
  isHost,
  isMuted,
  isVideoOff,
  isRecording,
  activePanel,
  hasUnreadMessages,
  savingSnapshot,
  showMobileMenu,
  isGuestMeasuring = false,
  isTranslationEnabled = false,
  isTranslating = false,
  hostLanguage: hostLanguageProp = 'en',
  guestLanguage: guestLanguageProp = 'es',
  toggleTranslation,
  onStartTranslation,
  setShowMobileMenu,
  setActivePanel,
  setHasUnreadMessages,
  toggleMute,
  toggleVideo,
  toggleRecording,
  takeSnapshot,
  switchCamera,
  handleEndCall,
  setIsAnnotating,
  setSelectedAnnotationTool,
}: VideoCallToolbarProps) {
  const [showEndConfirm, setShowEndConfirm] = useState(false);
  const touchHandledRef = useRef(false);

  // Language selector popover state
  const [showLangPopover, setShowLangPopover] = useState(false);
  const [selectedHostLang, setSelectedHostLang] = useState(hostLanguageProp);
  const [selectedGuestLang, setSelectedGuestLang] = useState(guestLanguageProp);
  const langPopoverRef = useRef<HTMLDivElement>(null);

  // Sync lang state if props change
  useEffect(() => { setSelectedHostLang(hostLanguageProp); }, [hostLanguageProp]);
  useEffect(() => { setSelectedGuestLang(guestLanguageProp); }, [guestLanguageProp]);

  // Close popover on outside click
  useEffect(() => {
    if (!showLangPopover) return;
    const handler = (e: MouseEvent) => {
      if (langPopoverRef.current && !langPopoverRef.current.contains(e.target as Node)) {
        setShowLangPopover(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showLangPopover]);

  const handleEndCallClick = () => {
    setShowEndConfirm(true);
  };


  return (
    <>
      {/* Desktop - Bottom Floating Glass Toolbar */}
      {!isMobileView && (
        <div 
          className="fixed bottom-8 left-1/2 -translate-x-1/2 z-[45]"
          style={{
            paddingBottom: 'max(0px, env(safe-area-inset-bottom))',
          }}
        >
          <div className="bg-black/40 backdrop-blur-xl border border-white/20 rounded-full shadow-2xl px-4 py-3 flex items-center justify-center gap-2">
            {/* Left Tools */}
            {isHost && (
              <div className="flex items-center gap-1 pr-3 border-r border-white/20">
                <button
                  data-panel-trigger="chat"
                  onClick={() => {
                    setActivePanel(activePanel === 'chat' ? null : 'chat');
                    if (activePanel !== 'chat') setHasUnreadMessages(false);
                  }}
                  className={`relative w-11 h-11 rounded-full flex items-center justify-center text-white hover:bg-white/20 transition-all ${activePanel === 'chat' ? 'bg-white/30' : ''}`}
                  title="Chat"
                >
                  <MessageSquare className="w-5 h-5" strokeWidth={2} />
                  {hasUnreadMessages && (
                    <div className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-red-500 rounded-full border border-black/40"></div>
                  )}
                </button>

                <button
                  onClick={takeSnapshot}
                  disabled={savingSnapshot}
                  className="w-11 h-11 rounded-full flex items-center justify-center text-white hover:bg-white/20 transition-all disabled:opacity-50"
                  title={savingSnapshot ? "Saving..." : "Snapshot"}
                >
                  {savingSnapshot ? (
                    <Loader2 className="w-5 h-5 animate-spin" strokeWidth={2} />
                  ) : (
                    <Camera className="w-5 h-5" strokeWidth={2} />
                  )}
                </button>

                <button
                  data-panel-trigger="notes"
                  onClick={() => setActivePanel(activePanel === 'notes' ? null : 'notes')}
                  className={`w-11 h-11 rounded-full flex items-center justify-center text-white hover:bg-white/20 transition-all ${activePanel === 'notes' ? 'bg-white/30' : ''}`}
                  title="Notes"
                >
                  <FileText className="w-5 h-5" strokeWidth={2} />
                </button>

                <button
                  data-panel-trigger="annotate"
                  onClick={() => {
                    const opening = activePanel !== 'annotate';
                    setActivePanel(opening ? 'annotate' : null);
                    if (opening) {
                      setIsAnnotating(true);
                      setSelectedAnnotationTool('pencil');
                    } else {
                      setIsAnnotating(false);
                    }
                  }}
                  className={`w-11 h-11 rounded-full flex items-center justify-center text-white hover:bg-white/20 transition-all ${activePanel === 'annotate' ? 'bg-white/30' : ''}`}
                  title="Draw"
                >
                  <Edit3 className="w-5 h-5" strokeWidth={2} />
                </button>

                {/* Measurement panel removed for host - guests use Ruler icon in their toolbar */}

                <button
                  onClick={toggleRecording}
                  className={`relative w-11 h-11 rounded-full flex items-center justify-center text-white hover:bg-white/20 transition-all ${isRecording ? 'bg-red-500/30' : ''}`}
                  title={isRecording ? 'Stop Recording' : 'Start Recording'}
                >
                  {isRecording ? (
                    <Square className="w-5 h-5 text-red-400 fill-red-400" strokeWidth={2} />
                  ) : (
                    <Circle className="w-5 h-5 text-red-400" strokeWidth={2} />
                  )}
                </button>

                {/* Live Translation - Host Only */}
                <div className="relative" ref={langPopoverRef}>
                  <button
                    onClick={() => {
                      if (isTranslationEnabled) {
                        toggleTranslation?.();
                      } else {
                        setShowLangPopover(prev => !prev);
                      }
                    }}
                    className={`relative w-11 h-11 rounded-full flex items-center justify-center text-white hover:bg-white/20 transition-all ${isTranslationEnabled ? 'bg-blue-500/40' : ''}`}
                    title={isTranslationEnabled ? 'Stop Live Translation' : 'Start Live Translation'}
                  >
                    <Languages className="w-5 h-5" strokeWidth={2} />
                    {isTranslating && (
                      <div className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-blue-400 rounded-full border border-black/40 animate-pulse" />
                    )}
                  </button>

                  {/* Language selector popover */}
                  {showLangPopover && !isTranslationEnabled && (
                    <div className="absolute bottom-14 left-1/2 -translate-x-1/2 bg-gray-900 border border-white/20 rounded-xl shadow-2xl p-4 w-56 z-50">
                      <p className="text-white text-xs font-semibold mb-3 text-center">Live Translation</p>
                      <div className="space-y-2 mb-4">
                        <div>
                          <label className="text-white/60 text-xs block mb-1">I speak</label>
                          <select
                            value={selectedHostLang}
                            onChange={e => setSelectedHostLang(e.target.value)}
                            className="w-full bg-white/10 text-white text-sm rounded-lg px-3 py-1.5 border border-white/20 focus:outline-none focus:border-blue-400"
                          >
                            {LANGUAGES.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="text-white/60 text-xs block mb-1">They speak</label>
                          <select
                            value={selectedGuestLang}
                            onChange={e => setSelectedGuestLang(e.target.value)}
                            className="w-full bg-white/10 text-white text-sm rounded-lg px-3 py-1.5 border border-white/20 focus:outline-none focus:border-blue-400"
                          >
                            {LANGUAGES.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
                          </select>
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          onStartTranslation?.(selectedHostLang, selectedGuestLang);
                          setShowLangPopover(false);
                        }}
                        className="w-full bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium py-2 rounded-lg transition-colors"
                      >
                        Start Translation
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Center Controls */}
            <div className="flex items-center gap-1">
              <button
                onClick={toggleMute}
                className={`w-12 h-12 rounded-full flex items-center justify-center text-white transition-all ${isMuted ? 'bg-red-500 hover:bg-red-600' : 'bg-white/20 hover:bg-white/30'}`}
                title={isMuted ? 'Unmute' : 'Mute'}
              >
                {isMuted ? <MicOff className="w-6 h-6" strokeWidth={2} /> : <Mic className="w-6 h-6" strokeWidth={2} />}
              </button>

              <button
                onClick={toggleVideo}
                className={`w-12 h-12 rounded-full flex items-center justify-center text-white transition-all ${isVideoOff ? 'bg-red-500 hover:bg-red-600' : 'bg-white/20 hover:bg-white/30'}`}
                title={isVideoOff ? 'Start Video' : 'Stop Video'}
              >
                {isVideoOff ? <VideoOff className="w-6 h-6" strokeWidth={2} /> : <Video className="w-6 h-6" strokeWidth={2} />}
              </button>

              {/* End Call - Phone Down Icon */}
              <button
                onClick={handleEndCallClick}
                className="w-14 h-14 rounded-full flex items-center justify-center bg-red-600 hover:bg-red-700 text-white transition-all mx-2 shadow-lg"
                title={isHost ? "End Call" : "Leave Call"}
              >
                <Phone className="w-7 h-7 rotate-[135deg]" strokeWidth={2.5} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mobile - Bottom Controls (compact in landscape to maximize video area) */}
      {isMobileView && (
        <div
          className={`mobile-toolbar-landscape fixed bottom-0 left-0 right-0 bg-gradient-to-t from-black via-black/95 to-transparent landscape:bg-transparent landscape:from-transparent landscape:via-transparent pt-4 landscape:pt-2 z-[85] transition-all duration-200 ${
            activePanel === 'chat' || activePanel === 'notes' || activePanel === 'annotate' || activePanel === 'measure' || isGuestMeasuring ? 'opacity-20 blur-md' : 'opacity-100'
          }`}
          style={{
            paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom, 24px))',
            pointerEvents: activePanel === 'chat' || activePanel === 'notes' || activePanel === 'annotate' || activePanel === 'measure' || isGuestMeasuring ? 'none' : 'auto', // Disable when panels are open or measuring
          }}
        >
          <style>{`
            @media (orientation: landscape) {
              .mobile-toolbar-landscape {
                padding-bottom: max(0.5rem, env(safe-area-inset-bottom, 8px)) !important;
              }
            }
          `}</style>
          <div
            className="overflow-x-auto overflow-y-visible w-full flex justify-center landscape:py-0.5"
            style={{
              WebkitOverflowScrolling: 'touch',
              scrollbarWidth: 'none', // Firefox
              msOverflowStyle: 'none', // IE/Edge
            }}
          >
            <style>{`
              .toolbar-scroll::-webkit-scrollbar {
                display: none; /* Chrome/Safari */
              }
            `}</style>
            <div className="toolbar-scroll flex items-center gap-3 landscape:gap-2 px-4 landscape:px-2">
            <button
              onClick={toggleMute}
              className="flex flex-col items-center gap-1 landscape:gap-0.5"
            >
              <div className={`w-14 h-14 landscape:w-10 landscape:h-10 rounded-full flex items-center justify-center shadow-xl transition-all ${isMuted ? 'bg-red-500' : 'bg-white/20 backdrop-blur-sm'}`}>
                {isMuted ? <MicOff className="h-6 w-6 landscape:h-4 landscape:w-4 text-white" strokeWidth={2} /> : <Mic className="h-6 w-6 landscape:h-4 landscape:w-4 text-white" strokeWidth={2} />}
              </div>
              <span className="text-[11px] landscape:text-[9px] text-white font-medium">Mic</span>
            </button>
            
            <button
              onClick={toggleVideo}
              className="flex flex-col items-center gap-1 landscape:gap-0.5"
            >
              <div className={`w-14 h-14 landscape:w-10 landscape:h-10 rounded-full flex items-center justify-center shadow-xl transition-all ${isVideoOff ? 'bg-red-500' : 'bg-white/20 backdrop-blur-sm'}`}>
                {isVideoOff ? <VideoOff className="h-6 w-6 landscape:h-4 landscape:w-4 text-white" strokeWidth={2} /> : <Video className="h-6 w-6 landscape:h-4 landscape:w-4 text-white" strokeWidth={2} />}
              </div>
              <span className="text-[11px] landscape:text-[9px] text-white font-medium">Video</span>
            </button>

            {/* Mobile End Button - Phone Down */}
            <button
              onClick={handleEndCallClick}
              className="flex flex-col items-center gap-1 landscape:gap-0.5"
            >
              <div className="w-16 h-16 landscape:w-12 landscape:h-12 rounded-full flex items-center justify-center bg-red-600 shadow-2xl">
                <Phone className="h-7 w-7 landscape:h-5 landscape:w-5 text-white rotate-[135deg]" strokeWidth={2.5} />
              </div>
              <span className="text-[11px] landscape:text-[9px] text-white font-semibold">End</span>
            </button>

            <button
              onClick={switchCamera}
              className="flex flex-col items-center gap-1 landscape:gap-0.5"
            >
              <div className="w-14 h-14 landscape:w-10 landscape:h-10 rounded-full flex items-center justify-center bg-white/20 backdrop-blur-sm shadow-xl">
                <RefreshCcw className="h-6 w-6 landscape:h-4 landscape:w-4 text-white" strokeWidth={2} />
              </div>
              <span className="text-[11px] landscape:text-[9px] text-white font-medium">Flip</span>
            </button>

            {/* 3-Dot Menu Button (Host Mobile) - Higher Z-Index */}
            {isHost && (
              <div className="relative z-[120]" data-mobile-menu>
                <button
                  data-mobile-menu-trigger
                  onTouchEnd={(e) => {
                    console.log('📱 [VideoCallToolbar] Touch END on More button', { showMobileMenu });
                    e.preventDefault();
                    e.stopPropagation();
                    touchHandledRef.current = true;
                    const newState = !showMobileMenu;
                    setShowMobileMenu(newState);
                    console.log('📱 [VideoCallToolbar] More menu toggled via onTouchEnd:', newState);
                    // Reset flag after a short delay
                    setTimeout(() => {
                      touchHandledRef.current = false;
                    }, 100);
                  }}
                  onClick={(e) => {
                    console.log('📱 [VideoCallToolbar] onClick fired', { showMobileMenu, touchHandled: touchHandledRef.current });
                    // Prevent onClick from firing if touch already handled it
                    if (touchHandledRef.current) {
                      console.log('📱 [VideoCallToolbar] onClick blocked - already handled by touch');
                      return;
                    }
                    e.stopPropagation();
                    setShowMobileMenu(!showMobileMenu);
                    console.log('📱 [VideoCallToolbar] More menu toggled via onClick:', !showMobileMenu);
                  }}
                  className="flex flex-col items-center gap-1 landscape:gap-0.5"
                >
                  <div className="w-14 h-14 landscape:w-10 landscape:h-10 rounded-full flex items-center justify-center bg-white/20 backdrop-blur-sm shadow-xl">
                    <MoreVertical className="h-6 w-6 landscape:h-4 landscape:w-4 text-white" strokeWidth={2} />
                  </div>
                  <span className="text-[11px] landscape:text-[9px] text-white font-medium">More</span>
                </button>

                {/* More Menu Dropdown - ABOVE PIP */}
                {showMobileMenu && (
                  <div
                    className="fixed bottom-[120px] right-4 bg-white rounded-2xl shadow-2xl py-2 min-w-[160px] border border-gray-200 z-[130]"
                    style={{
                      pointerEvents: 'auto',
                    }}
                    onClick={(e) => e.stopPropagation()}
                    ref={(el) => {
                      if (el) {
                        const rect = el.getBoundingClientRect();
                        const styles = window.getComputedStyle(el);
                        const data = {
                          y: Math.round(rect.y),
                          top: Math.round(rect.top),
                          height: Math.round(rect.height),
                          screenHeight: window.innerHeight,
                          isOffScreen: rect.top < 0 || rect.bottom < 0
                        };
                        console.log('📱 [VideoCallToolbar] Dropdown at Y:', data.y, 'Top:', data.top, 'Height:', data.height, 'ScreenH:', data.screenHeight, 'OffScreen:', data.isOffScreen);

                        // Also log to make it visible
                        if (data.isOffScreen) {
                          console.warn('🚨 DROPDOWN IS OFF-SCREEN! Position:', data);
                        }
                      }
                    }}
                  >
                    <button
                      onClick={() => {
                        setActivePanel(activePanel === 'chat' ? null : 'chat');
                        if (activePanel !== 'chat') setHasUnreadMessages(false);
                        setShowMobileMenu(false);
                      }}
                      onTouchEnd={(e) => {
                        e.preventDefault();
                        setActivePanel(activePanel === 'chat' ? null : 'chat');
                        if (activePanel !== 'chat') setHasUnreadMessages(false);
                        setShowMobileMenu(false);
                      }}
                      className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-100 transition-colors text-left"
                    >
                      <MessageSquare className="h-5 w-5 text-gray-700" />
                      <span className="text-sm font-medium text-gray-900">Chat</span>
                      {hasUnreadMessages && (
                        <div className="ml-auto w-2 h-2 bg-red-500 rounded-full"></div>
                      )}
                    </button>

                    <button
                      onClick={() => {
                        takeSnapshot();
                        setShowMobileMenu(false);
                      }}
                      onTouchEnd={(e) => {
                        e.preventDefault();
                        takeSnapshot();
                        setShowMobileMenu(false);
                      }}
                      disabled={savingSnapshot}
                      className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-100 transition-colors text-left disabled:opacity-50"
                    >
                      {savingSnapshot ? (
                        <Loader2 className="h-5 w-5 text-gray-700 animate-spin" />
                      ) : (
                        <Camera className="h-5 w-5 text-gray-700" />
                      )}
                      <span className="text-sm font-medium text-gray-900">
                        {savingSnapshot ? 'Saving...' : 'Snapshot'}
                      </span>
                    </button>

                    <button
                      onClick={() => {
                        setActivePanel(activePanel === 'notes' ? null : 'notes');
                        setShowMobileMenu(false);
                      }}
                      onTouchEnd={(e) => {
                        e.preventDefault();
                        setActivePanel(activePanel === 'notes' ? null : 'notes');
                        setShowMobileMenu(false);
                      }}
                      className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-100 transition-colors text-left"
                    >
                      <FileText className="h-5 w-5 text-gray-700" />
                      <span className="text-sm font-medium text-gray-900">Notes</span>
                    </button>

                    <button
                      onClick={() => {
                        const opening = activePanel !== 'annotate';
                        setActivePanel(opening ? 'annotate' : null);
                        if (opening) {
                          setIsAnnotating(true);
                          setSelectedAnnotationTool('pencil');
                        } else {
                          setIsAnnotating(false);
                        }
                        setShowMobileMenu(false);
                      }}
                      onTouchEnd={(e) => {
                        e.preventDefault();
                        const opening = activePanel !== 'annotate';
                        setActivePanel(opening ? 'annotate' : null);
                        if (opening) {
                          setIsAnnotating(true);
                          setSelectedAnnotationTool('pencil');
                        } else {
                          setIsAnnotating(false);
                        }
                        setShowMobileMenu(false);
                      }}
                      className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-100 transition-colors text-left"
                    >
                      <Edit3 className="h-5 w-5 text-gray-700" />
                      <span className="text-sm font-medium text-gray-900">Draw</span>
                    </button>

                    <button
                      onClick={() => {
                        toggleRecording();
                        setShowMobileMenu(false);
                      }}
                      onTouchEnd={(e) => {
                        e.preventDefault();
                        toggleRecording();
                        setShowMobileMenu(false);
                      }}
                      className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-100 transition-colors text-left"
                    >
                      <Circle className={`h-5 w-5 ${isRecording ? 'text-red-500' : 'text-gray-700'}`} />
                      <span className="text-sm font-medium text-gray-900">
                        {isRecording ? 'Stop Recording' : 'Record'}
                      </span>
                    </button>

                    {/* Live Translation - Host Mobile More Menu */}
                    {isTranslationEnabled ? (
                      <button
                        onClick={() => { toggleTranslation?.(); setShowMobileMenu(false); }}
                        onTouchEnd={(e) => { e.preventDefault(); toggleTranslation?.(); setShowMobileMenu(false); }}
                        className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-100 transition-colors text-left"
                      >
                        <Languages className="h-5 w-5 text-blue-500" />
                        <span className="text-sm font-medium text-gray-900">Stop Translation</span>
                        {isTranslating && <div className="ml-auto w-2 h-2 bg-blue-500 rounded-full animate-pulse" />}
                      </button>
                    ) : (
                      <div className="px-4 py-3 space-y-2">
                        <div className="flex items-center gap-3 mb-2">
                          <Languages className="h-5 w-5 text-gray-700 flex-shrink-0" />
                          <span className="text-sm font-medium text-gray-900">Live Translation</span>
                        </div>
                        <div className="flex gap-2">
                          <div className="flex-1">
                            <label className="text-xs text-gray-500 block mb-1">I speak</label>
                            <select
                              value={selectedHostLang}
                              onChange={e => setSelectedHostLang(e.target.value)}
                              className="w-full text-sm border border-gray-300 rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:border-blue-400"
                            >
                              <option value="en">English</option>
                              <option value="es">Spanish</option>
                            </select>
                          </div>
                          <div className="flex-1">
                            <label className="text-xs text-gray-500 block mb-1">They speak</label>
                            <select
                              value={selectedGuestLang}
                              onChange={e => setSelectedGuestLang(e.target.value)}
                              className="w-full text-sm border border-gray-300 rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:border-blue-400"
                            >
                              <option value="en">English</option>
                              <option value="es">Spanish</option>
                            </select>
                          </div>
                        </div>
                        <button
                          onClick={() => { onStartTranslation?.(selectedHostLang, selectedGuestLang); setShowMobileMenu(false); }}
                          onTouchEnd={(e) => { e.preventDefault(); onStartTranslation?.(selectedHostLang, selectedGuestLang); setShowMobileMenu(false); }}
                          className="w-full bg-blue-500 text-white text-sm font-medium py-2 rounded-lg hover:bg-blue-600 transition-colors"
                        >
                          Start Translation
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Guest Mobile - Chat and Measure */}
            {!isHost && (
              <>
                <button
                  onClick={() => {
                    setActivePanel(activePanel === 'chat' ? null : 'chat');
                    if (activePanel !== 'chat') setHasUnreadMessages(false);
                  }}
                  className="flex flex-col items-center gap-1"
                >
                  <div className={`relative w-14 h-14 rounded-full flex items-center justify-center shadow-xl transition-all ${activePanel === 'chat' ? 'bg-white/30' : 'bg-white/20 backdrop-blur-sm'}`}>
                    <MessageSquare className="h-6 w-6 text-white" strokeWidth={2} />
                    {hasUnreadMessages && (
                      <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full border border-black/40"></div>
                    )}
                  </div>
                  <span className="text-[11px] text-white font-medium">Chat</span>
                </button>

                {/* Measurement button removed for guests */}
              </>
            )}
            </div>
          </div>
        </div>
      )}

      {/* End Call Confirmation */}
      {showEndConfirm && (
        <div 
          className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[100] p-4"
          onClick={() => setShowEndConfirm(false)}
        >
          <div 
            className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center mb-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                {isHost ? 'End Call?' : 'Leave Call?'}
              </h3>
              <p className="text-gray-600">
                {isHost 
                  ? 'Are you sure you want to end this call for everyone?' 
                  : 'Are you sure you want to leave this call?'}
              </p>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => setShowEndConfirm(false)}
                className="flex-1 px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-900 font-semibold rounded-xl transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowEndConfirm(false);
                  handleEndCall();
                }}
                className="flex-1 px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-xl transition-colors"
              >
                {isHost ? 'End Call' : 'Leave'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Click outside to close More menu - MUST be below z-85 toolbar */}
      {showMobileMenu && (
        <div
          className="fixed inset-0 z-[84]"
          onClick={() => setShowMobileMenu(false)}
        />
      )}
    </>
  );
}